import React, { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { FiLock, FiCheckCircle } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from '@store/index'
import { logout } from '@store/slices/authSlice'
import { AuthService } from '@/services/authService'
import { User } from '@/types'
import { membershipApi } from '@/services/api'
import { ConsentService } from '@/services/consentService'
import { fetchUserIpAddress } from '@/utils/ipAddress'
import { AcknowledgementRecord } from '@/types/subscriptionAgreement'
import AcknowledgementPoint from '@/pages/AgencyProtral/Auth/Signup/steps/SubscriptionAgreement/AcknowledgementPoint'
import { isInitialFilled } from '@/pages/AgencyProtral/Auth/Signup/steps/SubscriptionAgreement/acknowledgementUtils'
import { HIPAA_ACKNOWLEDGEMENT_GROUPS, HIPAA_ACKNOWLEDGEMENT_POINTS } from './AgencyHippaAgreement/agencyHippaAgreementContent'
import AgencyHippaSigningModal from './AgencyHippaAgreement/AgencyHippaSigningModal'
import AgencyHippaAdministratorCertification from './AgencyHippaAgreement/AgencyHippaAdministratorCertification'
import LOGO from '@/assets/pillarThera.png'

interface AgencyHippaConsentDialogProps {
  open: boolean
  user: User
  hasSignedHIPAADocusignAgreement?: boolean
  onConsentSubmitted: () => void
}

// Existing template id used for the audit-trail consent record kept
// alongside the DocuSign envelope (same field the pre-DocuSign flow sent).
const AGREEMENT_TEMPLATE_ID = '686e3101582277712b6bb98c'
const AGREEMENT_VERSION = 'v2.1'
const TOTAL_POINTS = HIPAA_ACKNOWLEDGEMENT_POINTS.length

const AgencyHippaConsentDialog: React.FC<AgencyHippaConsentDialogProps> = ({
  open,
  user,
  hasSignedHIPAADocusignAgreement,
  onConsentSubmitted,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const agencyId = (user as any)?.agencyId || user.id

  const [modalOpen, setModalOpen] = useState(false)
  // Already signed via DocuSign (per the agency profile) — skip straight to
  // the acknowledgment step instead of asking them to sign again.
  const [signed, setSigned] = useState(!!hasSignedHIPAADocusignAgreement)
  const [signedAt, setSignedAt] = useState<string | null>(null)
  const [acknowledgements, setAcknowledgements] = useState<Record<string, AcknowledgementRecord>>({})
  const [administratorName, setAdministratorName] = useState(
    `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
  )
  // Fixed, read-only — matches how the subscription agreement flow sets it.
  const administratorTitle = 'Primary Administrator'
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null)
  const [eSignConsent, setESignConsent] = useState(false)
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const [ipAddress, setIpAddress] = useState('')
  const [submittingAgreement, setSubmittingAgreement] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    fetchUserIpAddress().then(setIpAddress)
  }, [])

  const handleInitialChange = (pointId: string, value: string) => {
    setAcknowledgements(prev => ({
      ...prev,
      [pointId]: {
        pointId,
        initials: value,
        timestamp: isInitialFilled(value) ? new Date().toISOString() : '',
      },
    }))
  }

  const handleSigned = () => {
    setModalOpen(false)
    setSigned(true)
    setSignedAt(new Date().toISOString())
  }

  // Shared by "Download unsigned/signed PDF" and "View agreement" — the
  // endpoint returns a presigned S3 URL for the PDF, which we just open in
  // a new tab (the browser's PDF viewer covers both viewing and saving).
  const handleOpenAgreementPdf = async () => {
    if (!agencyId) return
    setDownloadingPdf(true)
    try {
      const { signedUrl } = await membershipApi.downloadAgencyHippaAgreement(agencyId)
      window.open(signedUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('Failed to open HIPAA agreement PDF:', error)
    } finally {
      setDownloadingPdf(false)
    }
  }

  const handleLogout = async () => {
    try {
      await AuthService.agencyLogout()
    } catch (err) {
      console.error('Logout failed:', err)
    } finally {
      dispatch(logout())
      navigate('/agency-login')
    }
  }

  // Records the signed agreement (initials, certification, signature) as an
  // audit-trail entry alongside the DocuSign envelope, then unlocks the app.
  const handleSubmit = async () => {
    if (!agencyId || !signatureDataUrl) return
    setSubmitError(null)
    setSubmittingAgreement(true)
    try {
      const resolvedIpAddress = ipAddress || (await fetchUserIpAddress())
      await ConsentService.submitAgencyHippaConsent({
        userId: agencyId,
        agreementTemplateId: AGREEMENT_TEMPLATE_ID,
        agreementVersion: AGREEMENT_VERSION,
        fullName: administratorName,
        ipAddress: resolvedIpAddress,
        userAgent: navigator.userAgent,
        consentTime: new Date().toISOString(),
        signatureType: 'drawn',
        drawnSignature: signatureDataUrl,
        initial_1: acknowledgements['point-1']?.initials ?? '',
        initial_2: acknowledgements['point-2']?.initials ?? '',
        initial_3: acknowledgements['point-3']?.initials ?? '',
        initial_4: acknowledgements['point-4']?.initials ?? '',
        initial_5: acknowledgements['point-5']?.initials ?? '',
        initial_6: acknowledgements['point-6']?.initials ?? '',
        initial_7: acknowledgements['point-7']?.initials ?? '',
        initial_8: acknowledgements['point-8']?.initials ?? '',
        initial_9: acknowledgements['point-9']?.initials ?? '',
        initial_10: acknowledgements['point-10']?.initials ?? '',
        initial_11: acknowledgements['point-11']?.initials ?? '',
        initial_12: acknowledgements['point-12']?.initials ?? '',
        companyName: user.agencyName || '',
        agencyName: user.agencyName || '',
        adminName: administratorName,
        adminTitle: administratorTitle,
      })
      onConsentSubmitted()
    } catch (error: any) {
      console.error('Failed to record agency HIPAA consent:', error)
      setSubmitError(
        error?.response?.data?.message ||
          'Failed to save your signed agreement. Please try again.'
      )
    } finally {
      setSubmittingAgreement(false)
    }
  }

  const initialedCount = useMemo(
    () => HIPAA_ACKNOWLEDGEMENT_POINTS.filter(p => isInitialFilled(acknowledgements[p.id]?.initials || '')).length,
    [acknowledgements]
  )
  const allInitialed = initialedCount === TOTAL_POINTS

  // One signer, one set of initials — the first initials entered set the
  // standard, and every other point must match it exactly.
  const referenceInitials = useMemo(() => {
    const firstFilled = HIPAA_ACKNOWLEDGEMENT_POINTS.find(p => isInitialFilled(acknowledgements[p.id]?.initials || ''))
    return firstFilled ? acknowledgements[firstFilled.id].initials : null
  }, [acknowledgements])

  const allInitialsMatch = useMemo(
    () =>
      referenceInitials == null ||
      HIPAA_ACKNOWLEDGEMENT_POINTS.every(p => {
        const v = acknowledgements[p.id]?.initials || ''
        return !isInitialFilled(v) || v === referenceInitials
      }),
    [acknowledgements, referenceInitials]
  )

  const readyToSubmit =
    signed &&
    allInitialed &&
    allInitialsMatch &&
    administratorName.trim() !== '' &&
    administratorTitle.trim() !== '' &&
    !!signatureDataUrl &&
    eSignConsent

  const footnote = !signed
    ? 'Sign the agreement to begin.'
    : !allInitialed
      ? `${TOTAL_POINTS - initialedCount} acknowledgment${TOTAL_POINTS - initialedCount === 1 ? '' : 's'} left to initial.`
      : !allInitialsMatch
        ? 'Your initials must be the same on every point.'
        : !administratorName.trim() || !administratorTitle.trim()
        ? 'Add your name and title to finish.'
        : !signatureDataUrl
          ? 'Draw your signature to finish.'
          : !eSignConsent
            ? 'Confirm electronic signature consent to finish.'
            : 'All set — everything is complete.'

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return
      }}
      disableEscapeKeyDown
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          maxHeight: isMobile ? '100vh' : '95vh',
          borderRadius: isMobile ? 0 : 3,
          bgcolor: '#F6F8FA',
        },
      }}
    >
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Fixed header */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            bgcolor: 'white',
            borderBottom: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            py: { xs: 2, md: 1.75 },
            px: { xs: 2, md: 3 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box component="img" src={LOGO} alt="PillarThera Logo" sx={{ height: { xs: 34, md: 40 }, width: 'auto' }} />
          <Chip
            label={`${initialedCount} / ${TOTAL_POINTS} initialed`}
            sx={{
              bgcolor: allInitialed ? '#EAF7F0' : '#F3F5F7',
              color: allInitialed ? '#1E7A4E' : '#4B5563',
              fontWeight: 700,
              border: '1px solid',
              borderColor: allInitialed ? '#A9DCC0' : '#E6E9ED',
            }}
          />
        </Box>

        {/* Scrollable content */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, md: 4 }, py: { xs: 3, md: 3 } }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography sx={{ color: '#6F5FA6', fontWeight: 800, fontSize: { xs: '22px', md: '28px' }, letterSpacing: '-0.02em' }}>
              HIPAA Compliance Acknowledgment
            </Typography>
            <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#2C333D', mt: 0.75 }}>
              (Required)
            </Typography>
            <Typography sx={{ color: '#7C8794', fontSize: '15px', mt: 0.5 }}>
              Complete this step to continue using your agency&rsquo;s PillarThera account.
            </Typography>
          </Box>

          {/* Introduction */}
          <Box sx={{ bgcolor: 'white', border: '1px solid #E6E9ED', borderRadius: 2, p: { xs: 2.5, md: 3 }, mb: 2 }}>
            <Typography sx={{ fontSize: '15.5px', color: '#2C333D', mb: 1.5 }}>
              PillarThera handles Protected Health Information (PHI) for real patients receiving care in their
              homes. Before your agency can access patient records, schedules, visit documentation, or billing
              data, the person responsible for your account needs to sign the{' '}
              <strong>Agency Privacy, Security &amp; HIPAA Compliance Agreement</strong> and confirm they
              understand what it requires day to day.
            </Typography>
            <Typography sx={{ fontSize: '15.5px', color: '#2C333D', mb: 1.5 }}>
              This step has two parts. First you&rsquo;ll read and e-sign the full agreement, which is the
              binding contract between your agency and PillarThera. Then you&rsquo;ll initial twelve short
              statements that summarize the obligations your workforce will be held to. The summaries are there
              to make the agreement usable — they don&rsquo;t replace it, and the signed agreement governs if
              the two ever differ.
            </Typography>
            <Typography sx={{ fontSize: '14.5px', color: '#7C8794' }}>
              Only the <strong>Primary Agency Administrator</strong> can complete this step. You&rsquo;ll receive
              a signed copy by email, and it stays available in <em>Settings → Compliance Documents</em>.
            </Typography>
          </Box>

          {/* Step 1: sign agreement */}
          <Box sx={{ bgcolor: 'white', border: '1px solid #E6E9ED', borderRadius: 2, p: { xs: 2.5, md: 3 }, mb: 2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '18px', color: '#2C333D', mb: 0.5 }}>
              1. Read and sign the full agreement
            </Typography>
            <Typography sx={{ fontSize: '14.5px', color: '#7C8794', mb: 2 }}>
              Opens in a secure DocuSign window. You can download a copy before you sign and after.
            </Typography>

            {!signed ? (
              <>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    onClick={() => setModalOpen(true)}
                    disabled={!agencyId}
                    sx={{
                      bgcolor: '#6F5FA6',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 2,
                      px: 3,
                      '&:hover': { bgcolor: '#5B4C8F' },
                      '&.Mui-disabled': { bgcolor: 'grey.300', color: 'grey.500' },
                    }}
                  >
                    Open agreement &amp; sign
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleOpenAgreementPdf}
                    disabled={downloadingPdf}
                    startIcon={downloadingPdf ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : undefined}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3, borderColor: '#CFD6DE', color: '#2C333D' }}
                  >
                    {downloadingPdf ? 'Downloading…' : 'Download unsigned PDF'}
                  </Button>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 2,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: '#FEF6E7',
                    border: '1px solid #F0D7A4',
                    color: '#8A6414',
                    fontSize: '14px',
                    fontWeight: 600,
                  }}
                >
                  Not signed yet — required before you can continue
                </Box>
              </>
            ) : (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: '#EAF7F0',
                    border: '1px solid #A9DCC0',
                    color: '#1E7A4E',
                    fontSize: '14px',
                    fontWeight: 600,
                  }}
                >
                  <FiCheckCircle /> Signed — a copy has been emailed to you
                </Box>

                {signedAt && (
                  <Typography sx={{ mt: 1.5, fontSize: '13px', color: '#7C8794' }}>
                    Signed {new Date(signedAt).toLocaleString()} by the Primary Agency Administrator
                  </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleOpenAgreementPdf}
                    disabled={downloadingPdf}
                    startIcon={downloadingPdf ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : undefined}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3, borderColor: '#CFD6DE', color: '#2C333D' }}
                  >
                    {downloadingPdf ? 'Downloading…' : 'Download signed PDF'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleOpenAgreementPdf}
                    disabled={downloadingPdf}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3, borderColor: '#CFD6DE', color: '#2C333D' }}
                  >
                    View agreement
                  </Button>
                </Box>
              </Box>
            )}
          </Box>

          {!signed && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                bgcolor: '#F3F5F7',
                border: '1px dashed #CFD6DE',
                borderRadius: 2,
                p: 2,
                mb: 2,
                color: '#7C8794',
                fontSize: '14px',
              }}
            >
              <FiLock /> Sign the agreement above to unlock the acknowledgments.
            </Box>
          )}

          {/* Step 2: acknowledgments */}
          <Box sx={{ opacity: signed ? 1 : 0.42, pointerEvents: signed ? 'auto' : 'none', filter: signed ? 'none' : 'saturate(0.4)' }}>
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '18px', color: '#2C333D' }}>
                2. Initial each acknowledgment
              </Typography>
              <Typography sx={{ fontSize: '14.5px', color: '#7C8794' }}>
                Twelve statements, grouped into four areas. Enter your initials on each to confirm you understand it.
              </Typography>
            </Box>

            {HIPAA_ACKNOWLEDGEMENT_GROUPS.map(group => {
              const groupPoints = HIPAA_ACKNOWLEDGEMENT_POINTS.filter(p => p.group === group.id)
              const groupDone = groupPoints.filter(p => isInitialFilled(acknowledgements[p.id]?.initials || '')).length
              return (
                <Box key={group.id} sx={{ mt: 3.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      pb: 1,
                      mb: 1.25,
                      borderBottom: '2px solid #EEEBF7',
                    }}
                  >
                    <Typography sx={{ fontSize: '12.5px', fontWeight: 800, color: '#6F5FA6', textTransform: 'uppercase', letterSpacing: '0.09em' }}>
                      {group.label}
                    </Typography>
                    <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: groupDone === groupPoints.length ? '#1E7A4E' : '#7C8794' }}>
                      {groupDone} of {groupPoints.length}
                    </Typography>
                  </Box>
                  {groupPoints.map(point => (
                    <AcknowledgementPoint
                      key={point.id}
                      point={point}
                      value={acknowledgements[point.id]?.initials || ''}
                      onChange={handleInitialChange}
                      expectedInitials={referenceInitials}
                    />
                  ))}
                </Box>
              )
            })}

            <AgencyHippaAdministratorCertification
              agencyName={user.agencyName || ''}
              administratorName={administratorName}
              onAdministratorNameChange={setAdministratorName}
              administratorTitle={administratorTitle}
              hasSignature={!!signatureDataUrl}
              onSignatureChange={setSignatureDataUrl}
              eSignConsent={eSignConsent}
              onESignChange={setESignConsent}
            />
          </Box>

          {submitError && (
            <Alert severity="error" onClose={() => setSubmitError(null)} sx={{ mt: 2 }}>
              {submitError}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: { xs: 2, md: 2.5 },
          bgcolor: 'white',
          borderTop: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 -4px 12px rgba(0,0,0,0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography sx={{ fontSize: '13.5px', color: '#7C8794' }}>{footnote}</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            onClick={handleLogout}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              bgcolor: 'error.main',
              color: 'white',
              '&:hover': { bgcolor: 'error.dark' },
            }}
          >
            Cancel &amp; Log Out
          </Button>
          <Button
            variant="contained"
            disabled={!readyToSubmit || submittingAgreement}
            onClick={handleSubmit}
            startIcon={submittingAgreement ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : undefined}
            sx={{
              bgcolor: '#6F5FA6',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 4,
              '&:hover': { bgcolor: '#5B4C8F' },
              '&.Mui-disabled': { bgcolor: 'grey.300', color: 'grey.500' },
            }}
          >
            {submittingAgreement ? 'Submitting…' : 'Continue to dashboard'}
          </Button>
        </Box>
      </DialogActions>

      <AgencyHippaSigningModal
        open={modalOpen}
        agencyId={agencyId}
        onClose={() => setModalOpen(false)}
        onSigned={handleSigned}
      />
    </Dialog>
  )
}

export default AgencyHippaConsentDialog
